import React, { useEffect, useMemo, useRef, useState } from 'react';
import { acquireBodyScrollLock } from '../utils/bodyScrollLock';
import {
  copyTextToClipboard,
  downloadTextFile,
  formatCompactBuildList,
  type CompactBuildListMod,
} from '../utils/compactBuildList';

export interface ExportBuildListModalProps {
  isOpen: boolean;
  onClose: () => void;
  modifications: CompactBuildListMod[];
  vehicleLabel?: string;
  showPricingOption?: boolean;
}

export const ExportBuildListModal: React.FC<ExportBuildListModalProps> = ({
  isOpen,
  onClose,
  modifications,
  vehicleLabel = 'build',
  showPricingOption = false,
}) => {
  const [includeSpecifications, setIncludeSpecifications] = useState(true);
  const [includeDescriptions, setIncludeDescriptions] = useState(false);
  const [includeNotes, setIncludeNotes] = useState(false);
  const [includePrices, setIncludePrices] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  const preview = useMemo(
    () =>
      formatCompactBuildList(modifications, {
        includeSpecifications,
        includeDescriptions,
        includeNotes,
        includePrices: showPricingOption && includePrices,
      }),
    [
      modifications,
      includeSpecifications,
      includeDescriptions,
      includeNotes,
      includePrices,
      showPricingOption,
    ]
  );

  useEffect(() => {
    if (!isOpen) return;
    setStatus(null);
    const t = window.setTimeout(() => closeRef.current?.focus(), 50);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    return acquireBodyScrollLock();
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCopy = async () => {
    const ok = await copyTextToClipboard(preview);
    setStatus(ok ? 'Copied to clipboard' : 'Copy failed. Select the preview and copy manually');
  };

  const handleDownload = () => {
    const safe = vehicleLabel.replace(/[^\w-]+/g, '_').slice(0, 40) || 'build';
    downloadTextFile(`${safe}-build-list.txt`, preview);
    setStatus('Download started');
  };

  return (
    <div
      className="modal-overlay-sheet z-[70]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="export-build-list-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-sheet sm:max-w-2xl overflow-hidden flex flex-col">
        <div className="flex items-start justify-between gap-3 px-4 sm:px-5 py-4 border-b border-white/[0.06]">
          <div>
            <h2 id="export-build-list-title" className="text-lg font-semibold text-white">
              Copy or export build list
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Clean indented list for Notes, Docs, email, or show applications.
            </p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white rounded-lg min-h-[40px] min-w-[40px]"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="px-4 sm:px-5 py-3 flex flex-wrap gap-x-4 gap-y-2 border-b border-white/[0.06]">
          {(
            [
              ['Specs', includeSpecifications, setIncludeSpecifications],
              ['Descriptions', includeDescriptions, setIncludeDescriptions],
              ['Notes', includeNotes, setIncludeNotes],
            ] as const
          ).map(([label, checked, set]) => (
            <label key={label} className="inline-flex items-center gap-2 text-xs text-gray-300 min-h-[36px]">
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => set(e.target.checked)}
                className="checkbox-control"
              />
              {label}
            </label>
          ))}
          {showPricingOption && (
            <label className="inline-flex items-center gap-2 text-xs text-gray-300 min-h-[36px]">
              <input
                type="checkbox"
                checked={includePrices}
                onChange={(e) => setIncludePrices(e.target.checked)}
                className="checkbox-control"
              />
              Prices
            </label>
          )}
        </div>

        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y px-4 sm:px-5 py-3">
          <label htmlFor="export-preview" className="sr-only">
            Preview
          </label>
          <pre
            id="export-preview"
            className="whitespace-pre-wrap break-words text-xs sm:text-sm text-gray-200 font-mono leading-relaxed bg-black/30 rounded-xl border border-white/[0.06] p-3 min-h-[180px]"
          >
            {preview || 'Nothing to export yet.'}
          </pre>
        </div>

        {status && (
          <p className="px-4 sm:px-5 text-sm text-emerald-400" role="status">
            {status}
          </p>
        )}

        <div className="sticky bottom-0 flex flex-wrap gap-2 px-4 sm:px-5 py-4 border-t border-white/[0.06] bg-[#0c0c0c]">
          <button
            type="button"
            onClick={handleCopy}
            className="min-h-[44px] px-4 rounded-lg bg-orange-600 hover:bg-orange-500 text-white text-sm font-semibold"
          >
            Copy build list
          </button>
          <button
            type="button"
            onClick={handleDownload}
            className="min-h-[44px] px-4 rounded-lg border border-white/[0.10] text-gray-200 text-sm font-medium hover:bg-white/[0.04]"
          >
            Download text
          </button>
          <button
            type="button"
            onClick={onClose}
            className="min-h-[44px] px-4 rounded-lg text-gray-400 text-sm ml-auto"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportBuildListModal;
